import { z } from "zod";
import { RENEWAL_AT_SESSIONS } from "./renewals.ts";

/**
 * Sessions left on the attendance register (Asana "Attendance: sessions left on the register",
 * signed off 2026-10-29 in ai-personal-assistant `teamna/prototypes/attendance-sessions-left.html`).
 * Each name on the register shows a dashed bar: one dash for each session of the package. The API
 * sends the facts (`SessionsLeft`), and this one pure function turns them into the bar and its
 * words, so Desk web and the phone read one rule.
 *
 * Every person who takes attendance sees it, coaches too. Counts only, never a price.
 */
export const sessionsLeftSchema = z.object({
  /**
   * What pays for this session: a package (`package`), a month of a membership (`month`), or
   * nothing, because the family never had a package for this class (`none`).
   */
  kind: z.enum(["package", "month", "none"]),
  /** Sessions in the packages that pay for this class (owner changes included), or the month's limit. 0 with no limit. */
  size: z.number().int().min(0),
  /** Sessions left now. When this booking took a session, it is already off. */
  left: z.number().int().min(0),
  /**
   * Today's session: `taken` (the booking took one of the package), `owed` (the booking is an owed
   * session, a later package or paid month that covers this class, the same kind of class, pays it), `open` (no booking yet, or a booking that took
   * nothing: Came takes one), `none` (the booking takes nothing, such as one the owner let go).
   */
  today: z.enum(["taken", "owed", "open", "none"]),
  /** A package that pays for this class is paused on the session's day. */
  paused: z.boolean(),
  /** A package that pays for this class is not paid yet. */
  unpaid: z.boolean(),
  /** A membership month with no limit. */
  unlimited: z.boolean(),
  /**
   * The family whose packages pay. Siblings in one session share them: a Came on one child takes a
   * session from the bar of the other too (#ai-feedback 2026-10-30). Missing: shared with nobody.
   */
  pool: z.string().optional(),
});
export type SessionsLeft = z.infer<typeof sessionsLeftSchema>;

/** More sessions than this: one plain bar, not one dash for each session. */
export const SESSIONS_LEFT_MAX_DASHES = 16;

export type SessionsLeftTone = "ok" | "few" | "last" | "zero" | "none";
export type SessionsLeftDash = "used" | "today" | "open" | "left";

export type SessionsLeftView = {
  tone: SessionsLeftTone;
  /** One for each session, in order. Null for a plain bar (`usedShare`) or no bar. */
  dashes: SessionsLeftDash[] | null;
  /** A plain bar: the share used before today, 0 to 1. Null with dashes or no bar. */
  usedShare: number | null;
  /** The red striped dash after the bar: today, with no session to pay for it. */
  over: boolean;
  /** "6 left", "Last session", "No sessions left". The words of a row. */
  words: string;
  /** The card has room: "No sessions left. 8 of 8 used". */
  longWords: string;
  /** Grey tags next to the bar. */
  tags: Array<"Paused" | "Not paid">;
};

/** The bar and its words for one person on the register. */
export function sessionsLeftView(facts: SessionsLeft): SessionsLeftView {
  const tags: SessionsLeftView["tags"] = [];
  if (facts.paused) tags.push("Paused");
  if (facts.unpaid) tags.push("Not paid");
  if (facts.kind === "none") {
    return { tone: "none", dashes: null, usedShare: null, over: false, words: "No package", longWords: "No package", tags };
  }
  if (facts.kind === "month" && facts.unlimited) {
    return { tone: "ok", dashes: null, usedShare: null, over: false, words: "Membership", longWords: "Membership, no limit", tags };
  }

  const taken = facts.today === "taken" ? 1 : 0;
  const size = Math.max(facts.size, facts.left + taken);
  const used = Math.max(0, size - facts.left - taken);
  // Red only when today has no session to pay for it: an owed booking, or no booking and none left.
  const over = facts.today === "owed" || (facts.today === "open" && facts.left === 0);
  const month = facts.kind === "month" ? " this month" : "";

  let tone: SessionsLeftTone;
  let words: string;
  let longWords: string;
  if (over) {
    tone = "zero";
    words = "No sessions left";
    longWords = `No sessions left. ${used} of ${size} used${month}`;
  } else if (facts.left === 0 && taken) {
    tone = "last";
    words = "Last session";
    longWords = facts.kind === "month" ? "Last session this month" : "Last session today";
  } else {
    tone = facts.left <= RENEWAL_AT_SESSIONS ? "few" : "ok";
    words = `${facts.left} left${month}`;
    longWords = `${facts.left} ${facts.left === 1 ? "session" : "sessions"} left${month}`;
  }

  if (size === 0 || size > SESSIONS_LEFT_MAX_DASHES) {
    return { tone, dashes: null, usedShare: size === 0 ? 0 : used / size, over, words, longWords, tags };
  }
  const dashes: SessionsLeftDash[] = [];
  for (let i = 0; i < size; i++) {
    if (i < used) dashes.push("used");
    else if (i === used && taken) dashes.push("today");
    else if (i === used && facts.today === "open") dashes.push("open");
    else dashes.push("left");
  }
  return { tone, dashes, usedShare: null, over, words, longWords, tags };
}

/**
 * The facts after the answer on the register, before Save: Came on a person with no booking takes
 * one session, or makes an owed session when none is left (signed off 2026-10-29). So does Came on a
 * booking that took nothing (2026-10-30). Absent takes nothing. A booking keeps what it took:
 * attendance is a record.
 */
export function sessionsLeftAfter(facts: SessionsLeft, answer: "present" | "absent" | null | undefined): SessionsLeft {
  if (facts.today !== "open" || !answer) return facts;
  if (answer === "absent") return { ...facts, today: "none" };
  if (facts.kind === "none") return facts;
  if (facts.kind === "month" && facts.unlimited) return { ...facts, today: "taken" };
  return facts.left > 0 ? { ...facts, today: "taken", left: facts.left - 1 } : { ...facts, today: "owed" };
}

type Answer = "present" | "absent" | null | undefined;

/**
 * `sessionsLeftAfter` for everyone on the register at once. Siblings with the same `pool` share the
 * family's sessions: each Came takes one from the pool, so two brothers on a 12-session package
 * both read 10 left after both came, as the family page does after Save. In the order of the list,
 * the first children take the sessions that are left, and a child after them is owed.
 */
export function sessionsLeftAfterAll<P extends { bookingId: string; sessionsLeft?: SessionsLeft | null }>(
  people: readonly P[],
  answers: Readonly<Record<string, Answer>>,
): Map<string, SessionsLeft> {
  const takenFrom = new Map<string, number>();
  const firstPass = people.flatMap((person) => {
    const facts = person.sessionsLeft;
    if (!facts) return [];
    const pool = facts.pool;
    const before = pool ? (takenFrom.get(pool) ?? 0) : 0;
    const after = sessionsLeftAfter({ ...facts, left: Math.max(0, facts.left - before) }, answers[person.bookingId]);
    if (pool && facts.today === "open" && after.today === "taken" && !after.unlimited) takenFrom.set(pool, before + 1);
    return [{ person, facts, after }];
  });
  // Each bar shows what is left once every Came of the family is counted, not only the earlier ones.
  return new Map(
    firstPass.map(({ person, facts, after }) => {
      const taken = facts.pool ? (takenFrom.get(facts.pool) ?? 0) : 0;
      const shared = facts.pool && after.kind !== "none" && !after.unlimited ? { ...after, left: Math.max(0, facts.left - taken) } : after;
      return [person.bookingId, shared];
    }),
  );
}
